import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Case } from "@/lib/useRealtimeCases";
import { useCaseTasks } from "@/lib/CaseTasksContext";
import { useTeam } from "@/lib/useTeam";
import { caseTaskLabel, type CaseTask } from "@/lib/caseTasks";
import { firstNameOf, oldestTasksFirst, timeAgo } from "@/lib/kanbanTasks";
import { caseSummaryClient, caseSummaryTitle, useCaseSummaries } from "@/lib/useCaseSummaries";
import { useNow } from "@/lib/useNow";
import { TaskFlame } from "@/components/tasks/TaskFlame";

interface TodoTodayModalProps {
  isOpen: boolean;
  onClose: () => void;
  cases: Case[];
  onCaseClick: (caseData: Case) => void;
}

/** « À faire » : uniquement MES tâches ouvertes (plus anciennes d'abord). */
export function TodoTodayModal({ isOpen, onClose, cases, onCaseClick }: TodoTodayModalProps) {
  if (!isOpen) return null;
  return <TodoTasksDialog onClose={onClose} cases={cases} onCaseClick={onCaseClick} />;
}

function TodoTasksDialog({ onClose, cases, onCaseClick }: Omit<TodoTodayModalProps, "isOpen">) {
  const navigate = useNavigate();
  const { myOpenTasks, completeTask, reopenTask, loading } = useCaseTasks();
  const { profilesById } = useTeam();
  const now = useNow(30_000);
  const closeRef = useRef<HTMLButtonElement>(null);
  /** Tâches cochées depuis l'ouverture : restent affichées barrées (on peut se raviser). */
  const [doneHere, setDoneHere] = useState<CaseTask[]>([]);
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo(() => {
    const openIds = new Set(myOpenTasks.map((task) => task.id));
    return oldestTasksFirst([...myOpenTasks, ...doneHere.filter((task) => !openIds.has(task.id))]);
  }, [myOpenTasks, doneHere]);

  const caseIds = useMemo(() => rows.map((task) => task.case_id), [rows]);
  // Résumés relus à l'ouverture de la modale (nom / centre modifiés pendant la session).
  const [openedAt] = useState(() => Date.now());
  const summaries = useCaseSummaries(caseIds, cases, { freshSince: openedAt });
  const casesById = useMemo(() => new Map(cases.map((c) => [c.id, c])), [cases]);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus?.();
    };
  }, [onClose]);

  const toggle = async (task: CaseTask, done: boolean) => {
    setError(null);
    if (done) {
      setDoneHere((prev) => [...prev.filter((t) => t.id !== task.id), { ...task, completed_at: new Date().toISOString() }]);
      try {
        await completeTask(task.id);
      } catch (err) {
        setDoneHere((prev) => prev.filter((t) => t.id !== task.id));
        setError(err instanceof Error ? err.message : "Impossible de cocher la tâche.");
      }
    } else {
      try {
        await reopenTask(task.id);
        setDoneHere((prev) => prev.filter((t) => t.id !== task.id));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Impossible de rouvrir la tâche.");
      }
    }
  };

  const openCase = (caseId: string) => {
    const known = casesById.get(caseId);
    if (known) onCaseClick(known);
    else {
      onClose();
      navigate(`/reminders/case/${caseId}`);
    }
  };

  const openCount = myOpenTasks.length;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-gray-900/40" onClick={onClose} aria-hidden="true" />
      <div className="pointer-events-none fixed inset-0 z-50 flex items-start justify-center p-4 pt-[10vh]">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="todo-title"
          className="pointer-events-auto flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5"
        >
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-50 ring-1 ring-orange-100">
                <TaskFlame className="h-5 w-5" animated={openCount > 0} />
              </span>
              <div>
                <h2 id="todo-title" className="text-base font-semibold text-gray-900">À faire</h2>
                <p className="text-xs text-gray-500">
                  {loading ? "Chargement…" : openCount === 0 ? "Aucune tâche ouverte" : `${openCount} tâche${openCount > 1 ? "s" : ""} ouverte${openCount > 1 ? "s" : ""} · plus anciennes d'abord`}
                </p>
              </div>
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <p role="alert" className="border-b border-red-100 bg-red-50 px-5 py-2 text-sm text-red-700">{error}</p>
          )}

          <div className="flex-1 overflow-y-auto">
            {rows.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-14 text-center">
                <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-100">
                  <svg viewBox="0 0 24 24" className="h-7 w-7 text-emerald-600" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                </span>
                <h3 className="text-base font-semibold text-gray-900">{loading ? "Chargement de vos tâches…" : "Rien à faire pour l'instant"}</h3>
                {!loading && (
                  <p className="mt-1 max-w-sm text-sm text-gray-500">
                    Quand le manager vous assigne une tâche, elle apparaît ici et une flamme s'allume sur la carte du dossier.
                  </p>
                )}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {rows.map((task) => {
                  const done = !!task.completed_at;
                  const summary = summaries.get(task.case_id);
                  const client = caseSummaryClient(summary);
                  const author = firstNameOf(profilesById.get(task.created_by));
                  return (
                    <li key={task.id} className={`group flex items-start gap-3 px-5 py-3.5 ${done ? "bg-gray-50/60" : "hover:bg-gray-50"}`}>
                      <label className="mt-0.5 flex cursor-pointer items-center" title={done ? "Rouvrir" : "Marquer comme fait"}>
                        <input
                          type="checkbox"
                          checked={done}
                          onChange={(event) => void toggle(task, event.target.checked)}
                          className="h-[18px] w-[18px] cursor-pointer rounded-full border-gray-300 accent-emerald-600 focus-visible:ring-2 focus-visible:ring-indigo-500"
                          aria-label={`Fait : ${caseTaskLabel(task.task_type)} — ${caseSummaryTitle(summary)}`}
                        />
                      </label>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className={`text-sm font-semibold ${done ? "text-gray-400 line-through" : "text-gray-900"}`}>
                            {caseTaskLabel(task.task_type)}
                          </span>
                          <span className="text-xs text-gray-400">par {author} · {timeAgo(task.created_at, now)}</span>
                        </div>
                        <p className={`mt-0.5 truncate text-sm ${done ? "text-gray-400" : "text-gray-700"}`}>
                          <span className="font-medium">{caseSummaryTitle(summary)}</span>
                          {client && client !== caseSummaryTitle(summary) && <span className="text-gray-500"> · {client}</span>}
                          {summary?.center && (
                            <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-700">{summary.center}</span>
                          )}
                        </p>
                        {task.note && (
                          <p className={`mt-1.5 whitespace-pre-line rounded-md border-l-2 px-2.5 py-1.5 text-sm ${done ? "border-gray-200 text-gray-400" : "border-orange-300 bg-orange-50/60 text-gray-700"}`}>
                            {task.note}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => openCase(task.case_id)}
                        className="flex-shrink-0 rounded-md px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      >
                        Ouvrir le dossier
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
